import * as HLF from "html-living-framework";
import {Dict} from "../core/Reflection";
import {Signal} from "../core/Signal";
import {OptionId, SlotId} from "../data/StoryModel";
import {GameManager} from "../managers/GameManager";
import {FocusableButton} from "./FocusableButton";
import {Optionable, OptionableData} from "./Optionable";
import {SceneCreatorItem} from "./SceneCreatorItem";
import {StoryScene} from "./StoryScene";

// Define this outside of the class because we need it in the base class constructor
const onSelectSlot = Signal<(slotId: SlotId) => void>();

export class SceneCreatorScene extends StoryScene {

    public readonly onSelectSlot = onSelectSlot;

    private readonly _optionsBySlot: Dict<SlotId, OptionId[]> = {};

    constructor() {
        super(GameManager.instance.currentScene, {x: 2048, y: 1536});

        this.caption.backgroundVisible = false;
        this.updateSceneOptions();
        this._optionsBySlot = this.getOptionsBySlot();
    }

    public randomizeOptions() {
        this.gameManager.clearSelectedOptions();
        for (const p in this._optionsBySlot) {
            if (this._optionsBySlot.hasOwnProperty(p)) {
                const slotId = p as SlotId;
                const optionId = HLF.ArrayUtils.random(this._optionsBySlot[slotId]!);
                this.gameManager.selectOption(optionId, slotId);
            }
        }
        if (this.isSameCharacter()) {
            const character = this.gameManager.getSelectedOption("character");
            const options = this.gameManager.getOptions("characterOther").filter((it) => it !== character);
            this.gameManager.selectOption(HLF.ArrayUtils.random(options), "characterOther");
        }
        this.updateOptions();
    }

    public isSameCharacter(optionId?: OptionId, slotId?: SlotId) {
        const characterOption = this.gameManager.getSelectedOption("character");
        const characterOtherOption = this.gameManager.getSelectedOption("characterOther");
        if (optionId && slotId) {
            if (slotId === "character") {
                return characterOtherOption === optionId;
            }
            if (slotId === "characterOther") {
                return characterOption === optionId;
            }
            return false;
        } else {
            return characterOption === characterOtherOption;
        }
    }

    public getSceneSlotButtons() {
        return this.collectFromItems((it: Optionable, buttons: FocusableButton[]) => {
            if (it instanceof SceneCreatorItem && it.button) {
                buttons.push(it.button);
            }
        });
    }

    public getSceneSlot(slotId: SlotId) {
        return this.collectFromItems((it: Optionable, items: SceneCreatorItem[]) => {
            if (it instanceof SceneCreatorItem && it.slotIds && it.slotIds.indexOf(slotId) >= 0) {
                items.push(it);
            }
        })[0];
    }

    public destroy(): void {
        onSelectSlot.remove();
        super.destroy();
    }

    protected createSceneItem(data?: OptionableData): Optionable {
        return new SceneCreatorItem(onSelectSlot);
    }

    private updateSceneOptions() {
        const options = GameManager.instance.currentScene.options;
        options.splice(0, options.length);
        for (const option of this.getSceneOptions()) {
            const optionSlot = option.split(":");
            const optionId = optionSlot[0];
            const slotId = optionSlot[1] as SlotId;
            if (optionId && slotId) {
                if (!options.some((it) => it.slotId === slotId && it.optionId === optionId)) {
                    options.push({slotId, optionId});
                }
            }
        }
    }

    private getOptionsBySlot() {
        const optionsBySlot: Dict<SlotId, OptionId[]> = {};
        for (const option of GameManager.instance.currentScene.options) {
            if (option.slotId) {
                if (optionsBySlot[option.slotId]) {
                    optionsBySlot[option.slotId]!.push(option.optionId);
                } else {
                    optionsBySlot[option.slotId] = [option.optionId];
                }
            }
        }
        return optionsBySlot;
    }
}
